/**
 * Transport manager with priority-based fallback
 */

import type { TransportStrategy, TransportPayload } from './TransportStrategy.js';
import { FetchTransport } from './FetchTransport.js';
import { BeaconTransport } from './BeaconTransport.js';
import { XHRTransport } from './XHRTransport.js';

export class TransportManager {
  private strategies: TransportStrategy[];
  private debug: boolean;

  constructor(customStrategies: TransportStrategy[] = [], debug = false) {
    // Default strategies sorted by priority
    this.strategies = [
      new FetchTransport(),
      new BeaconTransport(),
      new XHRTransport(),
      ...customStrategies
    ].sort((a, b) => b.priority - a.priority);

    this.debug = debug;
  }

  /**
   * Send data using best available transport
   */
  async send(payload: TransportPayload): Promise<void> {
    const errors: Error[] = [];

    for (const strategy of this.strategies) {
      if (!strategy.isAvailable()) {
        this.log(`${strategy.name} not available, skipping`);
        continue;
      }

      try {
        this.log(`Attempting to send with ${strategy.name}`);
        await strategy.send(payload);
        this.log(`Successfully sent with ${strategy.name}`);
        return;
      } catch (error) {
        this.log(`Failed to send with ${strategy.name}: ${error}`);
        errors.push(error as Error);
      }
    }

    // All strategies failed
    throw new Error(
      `All transport strategies failed: ${errors.map(e => e.message).join(', ')}`
    );
  }

  /**
   * Send data synchronously (for page unload)
   * Tries beacon first, then XHR sync
   */
  sendSync(payload: TransportPayload): boolean {
    for (const strategy of this.strategies) {
      if (!strategy.isAvailable() || !strategy.sendSync) {
        continue;
      }

      try {
        const sent = strategy.sendSync(payload);
        if (sent) {
          this.log(`Successfully sent sync with ${strategy.name}`);
          return true;
        }
      } catch (error) {
        this.log(`Failed to send sync with ${strategy.name}: ${error}`);
      }
    }

    return false;
  }

  /**
   * Add a custom transport strategy
   */
  addStrategy(strategy: TransportStrategy): void {
    this.strategies.push(strategy);
    this.strategies.sort((a, b) => b.priority - a.priority);
  }

  /**
   * Get available strategies
   */
  getAvailableStrategies(): TransportStrategy[] {
    return this.strategies.filter(s => s.isAvailable());
  }

  private log(message: string): void {
    if (this.debug) {
      console.log(`[TransportManager] ${message}`);
    }
  }
}

// Export types and classes
export type { TransportStrategy, TransportPayload };
export { FetchTransport, BeaconTransport, XHRTransport };

